import type { Porch, PlacedPumpkin, Scene } from '../types';
import { GL, Program, Mesh, Target, MSTarget, program, mesh, drawMesh, freeMesh, target, freeTarget, msTarget, freeMS, FULLSCREEN_VS } from './gl';
import { V3, M4, v3, mat4, mul, perspective, lookAt, invert, placement, transform, rotateDir, clamp } from './math';
import * as S from './shaders';
import { Tier, TierSpec, SPECS } from './tier';
import { buildPorch, PORCH_DEFAULT, lightRig, heightAt, BARE, LAMP, WALL, DECK, DECK_X, GARDEN_SPOT, HOUSE_X, HOUSE_D } from '../scene/porch';
import { buildPumpkin, decodeSculpt, raycast, PumpkinGeometry, STRIDE } from '../pumpkin/shape';
import { drawDashes } from '../stencil/raster';
import { sheetMap, wallMesh, type SheetMap } from '../pumpkin/cutwall';
import { carveRaster, carveSize, setCarveSize, takeUploads, type CarveRaster } from '../stencil/carve';
import { buildStage, STAGE_LAMP, STAGE_SPOTS } from '../scene/stage';

/** the blank stage instead of the porch: each pool is floor x, z and radius; lamp is the spotlights' strength */
export interface Stage { spots: [number, number, number][]; lamp: number }

/** the stencil sheet as paper in the world, from flat in front of a pumpkin (wrap 0) to laid on its skin (1) */
export interface Paper { id: string; canvas: HTMLCanvasElement; wrap: number; alpha: number }

const MAXP = S.MAXP;
const UNITS: Record<string, number> = { uMasks: 0, uShadow: 1, uCells: 2, uDepth: 3, uSrc: 3, uLow: 3, uScene: 4, uBloom: 5, uBeams: 6, uPaper: 7, uZ: 8, uDash: 9 };

/** `fov` is the horizontal field of view in radians (default 0.95); `lens` slides the picture up or down without moving the eye: the target draws at that height on screen (-1..1); `lensX` the same sideways; `roll` tilts the picture about the view axis */
export interface Camera { yaw: number; pitch: number; dist: number; target: V3; lens?: number; lensX?: number; fov?: number; roll?: number }

/** photo mode's look, drawn in the display pass: a filter (see FILTERS), the focus distance in metres and the blur at its widest, as a share of the picture's height (0 is all sharp) */
export interface PhotoLook { filter: number; focus: number; blur: number }
export const FILTERS = ['None', 'Mono', 'Sepia', 'Night vision', 'Haunted'];

interface Slot {
  id: string;
  geo: PumpkinGeometry;
  mesh: Mesh; stem: Mesh; rig: Mesh;
  flames: V3[];          // pumpkin space
  src: V3;
  model: M4; inv: M4; centre: V3;
  keys: { shape: string; stencil: string; rig: string };
  /** its layer in the mask array, kept while it is off screen; -1 for none */
  layer: number;
  p: PlacedPumpkin;
  flick: number;         // smoothed flicker
  /** a wobble after a poke: tilt about the spot it sits on (x, z axes) and how fast it's tilting */
  wob: [number, number, number, number];
  /** how open its shell is, cut and scraped (carve.ts), and whether its top and stalk are cut off */
  open: [number, number];
  stalk: boolean;
  /** the cut face of the flesh: a strip on the carving's edges, and what it was built from */
  cut: { mesh: Mesh | null; map: SheetMap | null; geo: PumpkinGeometry | null; raster: CarveRaster | null; gen: number; wall: number };
}

interface Programs {
  prop: Program; pumpkin: Program; inside: Program; cut: Program; depth: Program; depthCut: Program; sky: Program; grass: Program; flame: Program; paper: Program;
  beams?: Program; down?: Program; up?: Program; display?: Program;
}

/**
 * The porch at night. Owns every GL object; `sync` brings it in line with a scene, `frame` draws one. Rebuilds its
 * programs and targets when the tier changes, and all of it when the context comes back after a loss.
 */
export class Renderer {
  gl: GL;
  tier: Tier = 'mid';
  spec: TierSpec = SPECS.mid;
  private lastT = 0;
  camera: Camera = { yaw: 0.25, pitch: 0.2, dist: 2.6, target: [0, DECK + 0.25, -0.6] };
  exposure = 1.3;
  /** draw calls and grass blades last frame, for the stats line and tests */
  stats = { draws: 0, blades: 0, w: 0, h: 0 };

  private P!: Programs;
  private porchMesh: Mesh | null = null;
  private stageMesh: Mesh | null = null;
  /** null: the porch */
  stage: Stage | null = null;
  /** the candles: off while choosing */
  lit = true;
  /** the stencil traced on the skin (ink, edge line, dashes); off, only what is carved shows */
  trace = true;
  /** the pumpkin whose stencil shows as a dashed outline (carving with the tools), or none */
  dots = '';
  /** a stencil mid-gesture, shown before it is the pumpkin's own */
  private previews = new Map<string, import('../types').Stencil>();
  private dash: { tex: WebGLTexture; canvas: HTMLCanvasElement; key: string; at: number } | null = null;
  paper: Paper | null = null;
  /** a lift off the ground per pumpkin: the hop of a copy, the drop through the stage */
  lift = new Map<string, number>();
  /** pumpkins kept out of the picture: not drawn, not picked, though their light stays (the porch zoomed in on the one being placed) */
  hidden = new Set<string>();
  private paperGL: { mesh: Mesh | null; tex: WebGLTexture | null; key: string; flat: Float32Array; skin: Float32Array; at: Float32Array } | null = null;
  private porch: Porch | null = null;
  private slots: Slot[] = [];
  /**
   * Pumpkins on the porch but not in the picture (the stage shows only the one being carved): meshes, cut wall and mask
   * layer kept, so going back to the porch is a lookup and not a rebuild of every carving. Oldest first
   */
  private parked = new Map<string, Slot>();
  /** each slot's mask layer, for the shaders (uLayer) */
  private layerOf = new Int32Array(MAXP);
  /** each pumpkin's carving over its whole shell (carve.ts), a layer each */
  private masks: WebGLTexture | null = null;
  /** which carving each layer holds, so a new one is sent whole and the same one only where it changed */
  private layerRaster: (CarveRaster | null)[] = [];
  /** sheet maps allowed per frame (`cutWall`); a one-off render lifts it to have every cut face in its only frame */
  mapBudget = 1;
  /** sheet maps still allowed this frame */
  private mapsLeft = 1;
  private shadow: Target | null = null;
  private ms: MSTarget | null = null;
  private hdr: Target | null = null;
  private beams: Target | null = null;
  /** drawing the stage floor's reflection */
  private under = false;
  private bloom: Target[] = [];
  private cells: WebGLTexture | null = null;
  private flameVao: WebGLVertexArrayObject | null = null;
  private flameBuf: WebGLBuffer | null = null;
  private emptyVao: WebGLVertexArrayObject | null = null;
  private vp: M4 = new Float32Array(16);
  private scratchVP: M4 = new Float32Array(16);
  private invVP: M4 = new Float32Array(16);
  private lampVP: M4 = new Float32Array(16);
  private flipM: M4 = new Float32Array(16);
  private spotVP = new Float32Array(48);
  private eye: V3 = [0, 1, 3];
  private fovy = 0.7;
  /** the vertical angle of view on screen, as last drawn */
  get fovY() { return this.fovy; }
  /** where the eye was, as last drawn */
  get eyeAt(): V3 { return this.eye; }
  /** photo mode's filter and focus; null draws the picture as it always is */
  photo: PhotoLook | null = null;
  private scale = 1;
  private lightData = {
    pc: new Float32Array(MAXP * 3), pl: new Float32Array(MAXP * 3), rot: new Float32Array(MAXP * 9),
    src: new Float32Array(MAXP * 3), geo: new Float32Array(MAXP * 4), open: new Float32Array(MAXP * 2),
  };

  constructor(public canvas: HTMLCanvasElement, tier: Tier) {
    const gl = canvas.getContext('webgl2', { antialias: false, alpha: false, depth: true, stencil: false, powerPreference: 'high-performance', preserveDrawingBuffer: false });
    if (!gl) throw new Error('no-webgl2');
    this.gl = gl;
    this.setTier(tier);
  }

  // ---- setup

  setTier(tier: Tier) {
    const gl = this.gl;
    const spec = { ...SPECS[tier] };
    if (spec.hdr && !gl.getExtension('EXT_color_buffer_float')) { tier = 'low'; Object.assign(spec, SPECS.low); }
    gl.getExtension('OES_texture_float_linear');
    const old = this.spec;
    this.tier = tier; this.spec = spec;
    this.compile();
    this.freeTargets();
    if (!this.masks || old.mask !== spec.mask) this.makeMasks(this.maskLayers);
    if (spec.shadow) {
      this.shadow = target(gl, spec.shadow, spec.shadow, { hdr: false, depth: true, color: false });
      gl.bindTexture(gl.TEXTURE_2D, this.shadow.depth);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_MODE, gl.COMPARE_REF_TO_TEXTURE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_FUNC, gl.LEQUAL);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    }
    if (!this.flameBuf) {
      this.flameBuf = gl.createBuffer();
      this.flameVao = gl.createVertexArray();
      gl.bindVertexArray(this.flameVao);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.flameBuf);
      for (const k of [0, 1]) {
        gl.enableVertexAttribArray(k);
        gl.vertexAttribPointer(k, 4, gl.FLOAT, false, 32, k * 16);
        gl.vertexAttribDivisor(k, 1);
      }
      this.emptyVao = gl.createVertexArray();
      gl.bindVertexArray(null);
    }
    // geometry detail follows the tier
    for (const s of this.slots) s.keys.shape = '';
    if (this.lastScene) this.sync(this.lastScene);
    this.w = 0;
  }

  private compile() {
    const gl = this.gl, sp = this.spec;
    const d = { SHADOWS: sp.shadow > 0, LDR: !sp.hdr, PLAIN_LIGHT: sp.plain, A2C: sp.msaa > 1 };
    const P: Programs = {
      prop: program(gl, S.PROP_VS, S.PROP_FS, d),
      pumpkin: program(gl, S.PUMPKIN_VS, S.PUMPKIN_FS, d),
      inside: program(gl, S.PUMPKIN_VS, S.INSIDE_FS, d),
      cut: program(gl, S.CUT_VS, S.CUT_FS, d),
      depth: program(gl, S.DEPTH_VS, S.DEPTH_FS),
      depthCut: program(gl, S.DEPTH_CUT_VS, S.DEPTH_CUT_FS),
      sky: program(gl, FULLSCREEN_VS, S.SKY_FS, d),
      grass: program(gl, S.GRASS_VS, S.GRASS_FS, d),
      flame: program(gl, S.FLAME_VS, S.FLAME_FS, d),
      paper: program(gl, S.PAPER_VS, S.PAPER_FS, d),
    };
    if (sp.hdr) {
      P.down = program(gl, FULLSCREEN_VS, S.BLOOM_DOWN_FS);
      P.up = program(gl, FULLSCREEN_VS, S.BLOOM_UP_FS);
      P.display = program(gl, FULLSCREEN_VS, S.DISPLAY_FS);
      if (sp.beams) P.beams = program(gl, FULLSCREEN_VS, S.BEAMS_FS, d);
    }
    if (this.P) for (const p of Object.values(this.P)) if (p) gl.deleteProgram(p.prog);
    for (const p of Object.values(P)) {
      if (!p) continue;
      gl.useProgram(p.prog);
      for (const [name, unit] of Object.entries(UNITS)) if (p.u[name]) gl.uniform1i(p.u[name], unit);
    }
    this.P = P;
  }

  /** layers in the mask array: one per pumpkin, grown in fours as the porch fills, so a few pumpkins don't pay for twelve */
  private maskLayers = 4;

  private makeMasks(layers: number, grow = false) {
    const gl = this.gl;
    // round the pumpkin twice as far as top to bottom; the flat sheet's 2048 became 3072 across the whole shell
    setCarveSize(this.spec.mask >= 2048 ? 3072 : 2048);
    const [w, h] = carveSize(), old = this.masks, had = this.maskLayers;
    this.maskLayers = layers;
    this.masks = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.masks);
    const levels = Math.floor(Math.log2(w)) + 1;
    gl.texStorage3D(gl.TEXTURE_2D_ARRAY, levels, gl.RGBA8, w, h, layers);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    if (old && grow) {
      // more room for a fuller porch: the layers come across on the GPU, rather than every carving sent again
      const fb = gl.createFramebuffer();
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fb);
      for (let l = 0; l < had; l++) {
        gl.framebufferTextureLayer(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, old, 0, l);
        gl.copyTexSubImage3D(gl.TEXTURE_2D_ARRAY, 0, 0, 0, l, 0, 0, w, h);
      }
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
      gl.deleteFramebuffer(fb);
      gl.generateMipmap(gl.TEXTURE_2D_ARRAY);
    } else {
      this.layerRaster = [];
      for (const s of [...this.slots, ...this.parked.values()]) { s.keys.stencil = ''; s.layer = -1; }
    }
    if (old) gl.deleteTexture(old);
  }

  private freeTargets() {
    const gl = this.gl;
    freeTarget(gl, this.shadow); this.shadow = null;
    this.freeScreenTargets();
  }

  private freeScreenTargets() {
    const gl = this.gl;
    freeMS(gl, this.ms); this.ms = null;
    freeTarget(gl, this.hdr); this.hdr = null;
    freeTarget(gl, this.beams); this.beams = null;
    this.bloom.forEach(t => freeTarget(gl, t)); this.bloom = [];
  }

  /** a fixed drawing size for a snapshot, in place of the canvas's size on screen */
  private forced: [number, number] | null = null;
  private w = 0;
  private h = 0;

  /** match the drawing buffer to the canvas's size on screen, at the tier's pixel ratio and the given scale */
  resize(scale = 1) {
    const gl = this.gl, c = this.canvas;
    const dpr = Math.min(devicePixelRatio || 1, this.spec.dpr) * scale;
    const w = this.forced ? this.forced[0] : Math.max(16, Math.round(c.clientWidth * dpr));
    const h = this.forced ? this.forced[1] : Math.max(16, Math.round(c.clientHeight * dpr));
    if (w === this.w && h === this.h && scale === this.scale) return;
    this.w = w; this.h = h; this.scale = scale;
    c.width = w; c.height = h;
    this.freeScreenTargets();
    if (!this.spec.hdr) return;
    const samples = Math.min(this.spec.msaa, (gl.getInternalformatParameter(gl.RENDERBUFFER, gl.RGBA16F, gl.SAMPLES) as Int32Array)?.[0] ?? 0);
    if (samples > 1) this.ms = msTarget(gl, w, h, true, samples);
    this.hdr = target(gl, w, h, { hdr: true, depth: true });
    if (this.spec.beams) this.beams = target(gl, w >> 1, h >> 1, { hdr: true });
    let bw = w, bh = h;
    for (let k = 0; k < 5; k++) { bw = Math.max(1, bw >> 1); bh = Math.max(1, bh >> 1); this.bloom.push(target(gl, bw, bh, { hdr: true })); }
  }

  // ---- the scene

  private lastScene: Scene | null = null;

  /** bring the GPU's copy in line with the scene: only what changed is rebuilt. `keep`: pumpkins to hold on to off screen */
  sync(scene: Scene, keep: string[] = []) {
    this.lastScene = scene;
    const gl = this.gl;
    // the night and the house's lean are fixed; a stored or live value is overridden here as well as on load
    const fixed: Porch = { ...scene.porch, mist: PORCH_DEFAULT.mist, moon: PORCH_DEFAULT.moon, jank: PORCH_DEFAULT.jank };
    const pk = JSON.stringify(fixed);
    if (!this.porchMesh || pk !== this.porchKey) {
      freeMesh(gl, this.porchMesh);
      const d = buildPorch(fixed);
      this.porchMesh = mesh(gl, d.v, [3, 3, 3, 1], d.i);
      this.porchKey = pk;
    }
    this.porch = fixed;
    const list = scene.pumpkins.slice(0, MAXP), ids = new Set(list.map(p => p.id)), kept = new Set(keep);
    // the ones that went: parked while the porch still has them, freed when it doesn't
    for (const s of this.slots) if (!ids.has(s.id)) { this.parked.delete(s.id); this.parked.set(s.id, s); }
    this.slots = this.slots.filter(s => ids.has(s.id));
    for (const p of list) { const s = this.parked.get(p.id); if (s) { this.parked.delete(p.id); this.slots.push(s); } }
    for (const [id, s] of this.parked) if (!kept.has(id)) { this.parked.delete(id); this.freeSlot(s); }
    const need = Math.min(MAXP, Math.max(4, Math.ceil((list.length + this.parked.size) / 4) * 4));
    if (need > this.maskLayers) { this.makeMasks(need, true); gl.activeTexture(gl.TEXTURE0); }
    // a layer each: its own if it has one, else a free one, else the one the longest parked holds
    const taken = new Set(this.slots.filter(s => ids.has(s.id) && s.layer >= 0).map(s => s.layer));
    const free = () => {
      const held = new Set([...taken, ...[...this.parked.values()].map(s => s.layer)]);
      for (let l = 0; l < this.maskLayers; l++) if (!held.has(l)) return l;
      const s = [...this.parked.values()].find(x => x.layer >= 0)!;
      const l = s.layer;
      s.layer = -1; s.keys.stencil = '';
      return l;
    };
    const next: Slot[] = [];
    let mips = false;
    list.forEach((p, i) => {
      let s = this.slots.find(x => x.id === p.id);
      const shapeKey = JSON.stringify(p.shape) + this.spec.detail + (p.scale ?? 1);
      if (!s || s.keys.shape !== shapeKey) {
        const geo = buildPumpkin(sized(p), decodeSculpt(p.shape.sculpt), this.spec.detail);
        s = this.fit(s, p, geo);
        s.keys.shape = shapeKey;
        s.keys.rig = '';
      }
      s.p = p;
      if (s.layer < 0 || s.layer >= this.maskLayers) { s.layer = free(); s.keys.stencil = ''; }
      taken.add(s.layer);
      this.layerOf[i] = s.layer;
      const rigKey = `${p.light.kind}:${p.light.count}:${p.wall}:${s.geo.minY}`;
      if (s.keys.rig !== rigKey) { this.makeRig(s); s.keys.rig = rigKey; }
      this.place(s);
      next.push(s);
      const sk = JSON.stringify(p.stencil);
      if (s.keys.stencil !== sk) { mips = this.uploadMask(s.layer, p, false) || mips; s.keys.stencil = sk; }
    });
    this.slots = next;
    // the mips for every layer at once: per layer, the first load built them for the whole array three times over
    if (mips) { gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.masks); gl.generateMipmap(gl.TEXTURE_2D_ARRAY); }
  }
  private porchKey = '';

  /** a pumpkin reshaped mid-gesture: the sculpt grid live, without encoding it */
  reshape(id: string, sculpt: Float32Array, detail: 'low' | 'high' = 'low') {
    const s = this.slots.find(x => x.id === id);
    if (!s) return;
    const geo = buildPumpkin(sized(s.p), sculpt, detail);
    this.fit(s, s.p, geo);
    s.keys.shape = '';
    this.makeRig(s);
    s.keys.rig = '';
    this.place(s);
  }

  /** the stencil changed for one pumpkin: redraw its mask now */
  restencil(id: string, preview?: import('../types').Stencil) {
    const i = this.slots.findIndex(x => x.id === id);
    if (i < 0) return;
    const p = this.slots[i].p;
    if (preview) this.previews.set(id, preview); else this.previews.delete(id);
    this.uploadMask(this.slots[i].layer, preview ? { ...p, stencil: preview } : p);
    if (preview) { this.slots[i].keys.stencil = ''; return; }
    this.slots[i].keys.stencil = JSON.stringify(this.slots[i].p.stencil);
  }

  private fit(s: Slot | undefined, p: PlacedPumpkin, geo: PumpkinGeometry): Slot {
    const gl = this.gl;
    const m = mesh(gl, geo.verts, [3, 3, 2, 1, 1, 3], geo.index);
    const st = mesh(gl, geo.stem.v, [3, 3, 3, 1], geo.stem.i);
    if (s) { freeMesh(gl, s.mesh); freeMesh(gl, s.stem); s.mesh = m; s.stem = st; s.geo = geo; return s; }
    return {
      id: p.id, geo, mesh: m, stem: st, rig: null as unknown as Mesh, flames: [], src: [0, 0, 0],
      model: new Float32Array(16), inv: new Float32Array(16), centre: [0, 0, 0],
      keys: { shape: '', stencil: '', rig: '' }, layer: -1, p, flick: 1, wob: [0, 0, 0, 0], open: [0, 0], stalk: false,
      cut: { mesh: null, map: null, geo: null, raster: null, gen: -1, wall: 0 },
    };
  }

  private makeRig(s: Slot) {
    const gl = this.gl, g = s.geo, p = s.p;
    const cols = g.cols, bottom = g.verts[(g.rows * (cols + 1)) * STRIDE + 1];
    const floor = Math.max(bottom, g.minY) + p.wall * 1.1;
    const rig = lightRig(p.light.kind, p.light.count, floor, g.radius);
    if (s.rig) freeMesh(gl, s.rig);
    s.rig = mesh(gl, rig.v, [3, 3, 3, 1], rig.i);
    s.flames = rig.at;
    s.src = rig.at.reduce((a, b) => v3.add(a, v3.scale(b, 1 / rig.at.length)), [0, 0, 0] as V3);
  }

  private place(s: Slot) {
    const [x, z] = s.p.at;
    s.centre = [x, (this.stage ? 0 : heightAt(x, z)) - s.geo.minY + (this.lift.get(s.id) ?? 0), z];
    s.model = placement(s.centre, s.p.turn);
    const [ax, az] = s.wob;
    if (ax || az) {
      // rock about the point it rests on: base, tilt, back up
      const a = Math.hypot(ax, az), k = [ax / a, 0, az / a], c = Math.cos(a), sn = Math.sin(a), t = 1 - c;
      const r = mat4(), b = s.geo.minY;
      r[0] = t * k[0] * k[0] + c; r[1] = sn * k[2]; r[2] = t * k[0] * k[2];
      r[4] = -sn * k[2]; r[5] = c; r[6] = sn * k[0];
      r[8] = t * k[0] * k[2]; r[9] = -sn * k[0]; r[10] = t * k[2] * k[2] + c;
      const down = mat4(); down[13] = -b;
      const up = mat4(); up[12] = s.centre[0]; up[13] = s.centre[1] + b; up[14] = s.centre[2];
      s.model = mul(up, mul(r, mul(down, placement([0, 0, 0], s.p.turn))));
    }
    s.inv = invert(s.model);
  }

  /** a shove at a point on the pumpkin (pumpkin space): tips it over its base, and a spring brings it back */
  nudge(id: string, q: V3, dir: V3) {
    const s = this.slots.find(x => x.id === id);
    if (!s) return;
    const at = transform(s.model, q), push = rotateDir(s.model, dir);
    const r = v3.sub(at, [s.centre[0], s.centre[1] + s.geo.minY, s.centre[2]]);
    // torque = r x push, about the x and z axes; heavier pumpkins (bigger radius) budge less
    const m = 1 / (s.geo.radius * 8);
    s.wob[2] += (r[1] * push[2] - r[2] * push[1]) * m * 25;
    s.wob[3] += (r[0] * push[1] - r[1] * push[0]) * m * 25;
  }

  private wobble(dt: number) {
    for (const s of this.slots) {
      const w = s.wob;
      if (!w[0] && !w[1] && !w[2] && !w[3]) continue;
      // a stiff, lightly damped spring: a few rocks and it settles
      const k = 260, c = 5;
      for (const i of [0, 1]) {
        w[i + 2] += (-k * w[i] - c * w[i + 2]) * dt;
        w[i] = Math.max(-0.35, Math.min(0.35, w[i] + w[i + 2] * dt));
      }
      if (Math.abs(w[0]) + Math.abs(w[1]) < 1e-4 && Math.abs(w[2]) + Math.abs(w[3]) < 1e-3) w.fill(0);
      this.place(s);
    }
  }

  private freeSlot(s: Slot) {
    const gl = this.gl;
    freeMesh(gl, s.mesh); freeMesh(gl, s.stem); freeMesh(gl, s.rig); freeMesh(gl, s.cut.mesh);
  }

  /** the strip of cut flesh on pumpkin s's carving, rebuilt when the carving's edges, the shape or the wall change */
  private cutWall(s: Slot, layer: number): Mesh | null {
    const r = this.layerRaster[layer], c = s.cut;
    if (!r) return null;
    if (c.raster === r && c.gen === r.edgeGen && c.geo === s.geo && c.wall === s.p.wall) return c.mesh;
    const fresh = c.geo !== s.geo || !c.map;
    // a sheet map takes ~20 ms: one a frame, so a porch of carved pumpkins comes in over a few frames, not one long one
    if (fresh && this.mapsLeft <= 0) return null;
    c.raster = r; c.gen = r.edgeGen; c.wall = s.p.wall;
    freeMesh(this.gl, c.mesh);
    c.mesh = null;
    if (!r.edges.some(Boolean)) return null;
    if (fresh) { c.map = sheetMap(s.geo, s.p.shape.squash); c.geo = s.geo; this.mapsLeft--; }
    const w = wallMesh(c.map!, r.W, r.H, r.edges, s.p.wall);
    if (w.i.length) c.mesh = mesh(this.gl, w.v, [3, 3, 1], w.i);
    return c.mesh;
  }

  /** a pumpkin's carving brought up to date and sent where it changed; the mips wait until the knife lifts */
  /** this pumpkin's carving into its layer; with `mips` off, says whether they want rebuilding (the caller does it once) */
  private uploadMask(layer: number, p: PlacedPumpkin, mips = true): boolean {
    const gl = this.gl, r = carveRaster(p.id, p.stencil), whole = this.layerRaster[layer] !== r;
    this.layerRaster[layer] = r;
    const s = this.slots.find(x => x.id === p.id);
    if (s) { s.open = r.open; s.stalk = r.stalk; }
    const ups = takeUploads(r, whole);
    if (!ups.length) return false;
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.masks);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    for (const u of ups) gl.texSubImage3D(gl.TEXTURE_2D_ARRAY, 0, u.x, u.y, layer, u.w, u.h, 1, gl.RGBA, gl.UNSIGNED_BYTE, u.data);
    if (r.draft) return false;
    if (mips) gl.generateMipmap(gl.TEXTURE_2D_ARRAY);
    return true;
  }

  /**
   * The dashed outline of the pumpkin in `dots`: its ink traced and dashed on the CPU (~15ms), so only when the ink
   * changes, carving never, and no more than every 120ms while a piece is dragged about
   */
  private updateDash(t: number) {
    const s = this.dots ? this.slots.find(x => x.id === this.dots) : undefined;
    if (!s) return;
    const st = this.previews.get(s.id) ?? s.p.stencil, key = s.id + JSON.stringify([st.parts, st.strokes, !!st.mirror]);
    if (this.dash?.key === key || (this.dash && t - this.dash.at < 0.12 && this.dash.key.startsWith(s.id))) return;
    const gl = this.gl, n = Math.min(1024, this.spec.mask);
    if (!this.dash) {
      const tex = gl.createTexture()!, canvas = document.createElement('canvas');
      this.dash = { tex, canvas, key: '', at: 0 };
    }
    const d = this.dash;
    if (d.canvas.width !== n) d.canvas.width = d.canvas.height = n;
    drawDashes(st, d.canvas.getContext('2d')!, n);
    gl.activeTexture(gl.TEXTURE9);
    gl.bindTexture(gl.TEXTURE_2D, d.tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, d.canvas);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.activeTexture(gl.TEXTURE0);
    d.key = key; d.at = t;
  }

  // ---- picking

  /** the eye ray through a point on the canvas, in world space */
  ray(clientX: number, clientY: number): { o: V3; d: V3 } {
    const r = this.canvas.getBoundingClientRect();
    const x = (clientX - r.left) / r.width * 2 - 1, y = 1 - (clientY - r.top) / r.height * 2;
    const a = transform(this.invVP, [x, y, -1]), b = transform(this.invVP, [x, y, 1]);
    return { o: a, d: v3.norm(v3.sub(b, a)) };
  }

  /** the nearest pumpkin under a point: its index, and the hit in its own space */
  pick(clientX: number, clientY: number) {
    const { o, d } = this.ray(clientX, clientY);
    let best: { index: number; id: string; hit: NonNullable<ReturnType<typeof raycast>> } | null = null;
    this.slots.forEach((s, index) => {
      if (this.hidden.has(s.id)) return;
      const lo = transform(s.inv, o), ld = v3.norm(rotateDir(s.inv, d));
      const hit = raycast(s.geo, lo, ld);
      if (hit && (!best || hit.t < best.hit.t)) best = { index, id: s.id, hit };
    });
    return best as { index: number; id: string; hit: NonNullable<ReturnType<typeof raycast>> } | null;
  }

  /** where a point on the canvas meets the level `y`: dragging a pumpkin by its middle, so the cursor can be over the wall */
  level(clientX: number, clientY: number, y: number): [number, number] | null {
    const { o, d } = this.ray(clientX, clientY);
    if (Math.abs(d[1]) < 1e-4) return null;
    const t = (y - o[1]) / d[1];
    return t > 0 ? [o[0] + d[0] * t, o[2] + d[2] * t] : null;
  }

  /**
   * Dragging on the level `y`, the grab offset `off` added: the spot under the pointer, kept on the porch along the line
   * from the eye. Near the horizon the plane is met far off, so clamping x and z apart slid it metres sideways as the
   * pointer went up; here it stops where the pointer's line leaves the porch, and above the horizon it goes to the far side
   */
  dragOnPorch(clientX: number, clientY: number, y: number, off: [number, number]): [number, number] {
    const { o, d } = this.ray(clientX, clientY);
    const t = d[1] < -1e-4 ? (y - o[1]) / d[1] : Infinity, flat = Math.hypot(d[0], d[2]) || 1;
    const e: [number, number] = [o[0] + off[0], o[2] + off[1]];
    const q: [number, number] = t < 200 ? [o[0] + d[0] * t + off[0], o[2] + d[2] * t + off[1]] : [e[0] + d[0] / flat * 200, e[1] + d[2] / flat * 200];
    const lo = Renderer.onPorch(-1e9, -1e9), hi = Renderer.onPorch(1e9, 1e9), p = [q[0] - e[0], q[1] - e[1]];
    let t0 = 0, t1 = 1;
    for (let k = 0; k < 2; k++) {
      if (Math.abs(p[k]) < 1e-9) { if (e[k] < lo[k] || e[k] > hi[k]) return Renderer.onPorch(q[0], q[1]); continue; }
      let a = (lo[k] - e[k]) / p[k], b = (hi[k] - e[k]) / p[k];
      if (a > b) [a, b] = [b, a];
      t0 = Math.max(t0, a); t1 = Math.min(t1, b);
    }
    return t0 <= t1 ? Renderer.onPorch(e[0] + p[0] * t1, e[1] + p[1] * t1) : Renderer.onPorch(q[0], q[1]);
  }

  /** keep a spot on the boards, steps or lawn */
  static onPorch(x: number, z: number): [number, number] { return [clamp(x, -DECK_X + 0.2, DECK_X - 0.2), clamp(z, WALL + 0.3, 6)]; }

  /** a pumpkin's centre on screen, in CSS pixels; for labels and the selection ring */
  screenOf(id: string): [number, number] | null {
    const s = this.slots.find(x => x.id === id);
    if (!s) return null;
    const c = transform(this.vp, s.centre), r = this.canvas.getBoundingClientRect();
    return [(c[0] * 0.5 + 0.5) * r.width, (0.5 - c[1] * 0.5) * r.height];
  }

  /** a world point on the canvas, in CSS pixels */
  project(p: V3): [number, number] {
    const c = transform(this.vp, p), r = this.canvas.getBoundingClientRect();
    return [(c[0] * 0.5 + 0.5) * r.width, (0.5 - c[1] * 0.5) * r.height];
  }

  /** world points on the canvas as camera `c` would show them, in CSS pixels; behind the eye they come out null */
  projectWith(c: Camera, pts: V3[]): ([number, number] | null)[] {
    const m = this.scratchVP, r = this.canvas.getBoundingClientRect();
    this.viewOf(c, m);
    return pts.map(p => {
      const w = m[3] * p[0] + m[7] * p[1] + m[11] * p[2] + m[15];
      if (w < 0.03) return null;
      const x = (m[0] * p[0] + m[4] * p[1] + m[8] * p[2] + m[12]) / w, y = (m[1] * p[0] + m[5] * p[1] + m[9] * p[2] + m[13]) / w;
      return [(x * 0.5 + 0.5) * r.width, (0.5 - y * 0.5) * r.height];
    });
  }

  /** where a point on the stencil sheet lands on a pumpkin's skin, in the world */
  sheetToWorld(id: string, x: number, y: number): V3 | null {
    const s = this.slots.find(q => q.id === id);
    if (!s) return null;
    const ph = x * 1.25, th = y, sq = s.p.shape.squash;
    const d = v3.norm([Math.sin(ph) * Math.cos(th), Math.sin(th) * sq, Math.cos(ph) * Math.cos(th)]);
    const hit = raycast(s.geo, v3.scale(d, 3), v3.scale(d, -1), true);
    return hit ? transform(s.model, hit.p) : null;
  }

  /** a pumpkin's foot and size, for the handles drawn round it */
  footOf(id: string): { at: V3; r: number; top: V3 } | null {
    const s = this.slots.find(q => q.id === id);
    if (!s) return null;
    const c = s.centre;
    return { at: [c[0], c[1] + s.geo.minY, c[2]], r: s.geo.radius, top: [c[0], c[1] + s.geo.radius * s.p.shape.squash * 1.1 + 0.04, c[2]] };
  }

  centreOf(id: string): V3 | null { return this.slots.find(x => x.id === id)?.centre ?? null; }

  geometry(id: string) { return this.slots.find(x => x.id === id)?.geo ?? null; }

  // ---- a frame

  private lightsAt(t: number) {
    const L = this.lightData;
    this.slots.forEach((s, i) => {
      const l = s.p.light;
      let k = l.brightness;
      if (l.kind === 'candle' || l.kind === 'candles') {
        const n = l.kind === 'candles' ? Math.max(2, Math.min(5, l.count)) : 1;
        // a flame breathes slowly and gutters now and then; several flames average out
        const f = 0.55 * Math.sin(t * 2.3 + i * 1.7) + 0.3 * Math.sin(t * 7.9 + i * 0.6) + 0.25 * Math.sin(t * 17.3 + i * 3.1)
          + 0.6 * Math.max(0, Math.sin(t * 0.83 + i * 2.2) - 0.8) * Math.sin(t * 31 + i);
        const target = 1 - l.flicker * 0.32 * (f / Math.sqrt(n) + 0.4);
        s.flick += (target - s.flick) * 0.5;
        k *= 0.07 * Math.pow(n, 0.7) * s.flick;
      } else if (l.kind === 'led') {
        k *= 0.06;
        s.flick = 1;
      } else {
        const ph = (t * l.rate + i * 0.37) % 1;
        s.flick = 0.04 + 0.96 * (ph < 0.5 ? 1 : 0) * (1 - l.flicker * 0.3 * Math.random());
        k *= 0.06 * s.flick;
      }
      // a bigger pumpkin is a bigger room: distances inside and out grow with its size, so the inverse square would
      // dim it; the light grows with the square of the scale and the glow and the throw look the same, larger
      k *= (s.p.scale ?? 1) ** 2;
      // while one is placed alone the hidden ones go dark too: only its own carving lands on the deck
      if (!this.lit || this.hidden.has(s.id)) k = 0;
      L.pc.set(s.centre, i * 3);
      L.pl.set([l.color[0] * k, l.color[1] * k, l.color[2] * k], i * 3);
      const c = Math.cos(s.p.turn), sn = Math.sin(s.p.turn);
      // world to pumpkin: the transpose of the placement's turn
      L.rot.set([c, 0, sn, 0, 1, 0, -sn, 0, c], i * 9);
      L.src.set(s.src, i * 3);
      const spread = l.kind === 'candles' ? 1.4 : l.kind === 'candle' ? 0.6 : 0;
      L.geo.set([s.geo.radius, s.p.shape.squash, spread + Math.log2(carveSize()[1] / Math.PI / 256), 3.0], i * 4);
      L.open.set(s.open, i * 2);
    });
  }

  private common(p: Program, t: number) {
    const gl = this.gl, u = p.u, L = this.lightData, po = this.porch!;
    const n = this.slots.length;
    gl.uniform3fv(u.uEye, this.eye);
    gl.uniform1f(u.uTime, t);
    gl.uniform1f(u.uExposure, this.exposure);
    gl.uniform1f(u.uUnder, this.under ? 1 : 0);
    const md = v3.norm([-0.45, 0.42, -0.78]);
    gl.uniform3fv(u.uMoonDir, md);
    const mk = 0.14 + 2.6 * po.moon;
    gl.uniform3f(u.uMoon, 0.05 * mk, 0.062 * mk, 0.1 * mk);
    gl.uniform3f(u.uSkyAmb, 0.011 * (0.28 + 0.8 * po.moon), 0.014 * (0.28 + 0.8 * po.moon), 0.028 * (0.28 + 0.8 * po.moon));
    gl.uniform3f(u.uGroundAmb, 0.004, 0.0035, 0.003);
    gl.uniform3fv(u.uLampPos, LAMP);
    const lk = po.lamp.on ? po.lamp.brightness * 0.125 : 0;
    gl.uniform3f(u.uLamp, po.lamp.color[0] * lk, po.lamp.color[1] * lk, po.lamp.color[2] * lk);
    gl.uniform1f(u.uWall, WALL);
    gl.uniformMatrix4fv(u.uLampVP, false, this.lampVP);
    gl.uniform1i(u.uNP, n);
    if (n) {
      gl.uniform3fv(u.uPC, L.pc.subarray(0, n * 3));
      gl.uniform3fv(u.uPL, L.pl.subarray(0, n * 3));
      gl.uniformMatrix3fv(u.uPRot, false, L.rot.subarray(0, n * 9));
      gl.uniform3fv(u.uPSrc, L.src.subarray(0, n * 3));
      gl.uniform4fv(u.uPGeo, L.geo.subarray(0, n * 4));
      if (u.uPOpen) gl.uniform2fv(u.uPOpen, L.open.subarray(0, n * 2));
      if (u.uLayer) gl.uniform1iv(u.uLayer, this.layerOf.subarray(0, n));
    }
    gl.uniform4f(u.uMist, 0.003, 0.0038, 0.007, po.mist);
    if (u.uShadowTexel && this.spec.shadow) gl.uniform1f(u.uShadowTexel, 1 / this.spec.shadow);
    gl.uniformMatrix4fv(u.uVP, false, this.vp);
    const st = this.stage;
    // the garden spot: the porch's one stage spot, in the first quarter of the shadow map
    gl.uniform1i(u.uNS, 1);
    gl.uniformMatrix4fv(u.uSpotVP, false, this.spotVP);
    gl.uniform3fv(u.uSpotPos, [...GARDEN_SPOT.at, 0, 0, 0, 0, 0, 0]);
    gl.uniform3fv(u.uSpotCol, [...GARDEN_SPOT.col, 0, 0, 0, 0, 0, 0]);
    if (st) {
      // the stage: no moon, no porch lamp, no mist; three spots on the gourds, left, middle and right
      gl.uniform3fv(u.uLampPos, STAGE_LAMP);
      gl.uniform3f(u.uLamp, 0, 0, 0);
      gl.uniform1i(u.uNS, 3);
      gl.uniformMatrix4fv(u.uSpotVP, false, this.spotVP);
      gl.uniform3fv(u.uSpotPos, STAGE_SPOTS.flatMap(x => x.at));
      gl.uniform3fv(u.uSpotCol, STAGE_SPOTS.flatMap(x => x.col.map(c => c * st.lamp)));
      gl.uniform1f(u.uWall, -50);
      gl.uniform3f(u.uMoon, 0.004, 0.0045, 0.006);
      gl.uniform3f(u.uSkyAmb, 0.002, 0.002, 0.0025);
      gl.uniform4f(u.uMist, 0, 0, 0, 0);
    }
  }

  /** a camera's eye, and its view-projection at this canvas's shape into `vp` */
  private viewOf(c: Camera, vp: M4): V3 {
    // before the first frame there is no buffer yet, but the canvas has its shape on screen
    const aspect = this.w ? this.w / this.h : (this.canvas.clientWidth || 1) / (this.canvas.clientHeight || 1), cp = Math.cos(c.pitch);
    const eye = v3.add(c.target, v3.scale([Math.sin(c.yaw) * cp, Math.sin(c.pitch), Math.cos(c.yaw) * cp], c.dist));
    // keep the horizontal view on a tall phone screen: widen the vertical instead of cropping the sides
    const fx = c.fov ?? 0.95;
    const fovy = aspect < 1.2 ? 2 * Math.atan(Math.tan(fx / 2) / aspect) : 2 * Math.atan(Math.tan(fx / 2) / 1.2);
    if (c === this.camera) this.fovy = fovy;
    const proj = perspective(fovy, aspect, 0.03, 80);
    proj[9] -= c.lens ?? 0;
    proj[8] -= c.lensX ?? 0;
    const view = lookAt(eye, c.target), r = c.roll ?? 0;
    if (r) {
      // turn the view about its own axis: rows x and y of the view matrix
      const cr = Math.cos(r), sr = Math.sin(r);
      for (let k = 0; k < 4; k++) { const a = view[k * 4], b = view[k * 4 + 1]; view[k * 4] = cr * a - sr * b; view[k * 4 + 1] = sr * a + cr * b; }
    }
    mul(proj, view, vp);
    return eye;
  }

  private updateCamera() {
    this.eye = this.viewOf(this.camera, this.vp);
    this.invVP = invert(this.vp);
    const lampAt: V3 = [LAMP[0], 0, 2.2];
    mul(perspective(2.3, 1, 0.15, 16), lookAt(LAMP, lampAt, [0, 0, -1]), this.lampVP);
    mul(perspective(1.05, 1, 0.5, 16), lookAt(GARDEN_SPOT.at, GARDEN_SPOT.aim), this.spotVP.subarray(0, 16) as M4);
  }

  frame(t: number) {
    const gl = this.gl, sp = this.spec;
    if (!this.porch || gl.isContextLost()) return;
    this.mapsLeft = this.mapBudget;
    if (this.stage && !this.stageMesh) { const d = buildStage(); this.stageMesh = mesh(gl, d.v, [3, 3, 3, 1], d.i); }
    this.resize(this.scale);
    this.updateCamera();
    if (this.stage) this.aimSpots();
    this.wobble(Math.min(1 / 30, Math.max(0, t - this.lastT)));
    this.lastT = t;
    this.lightsAt(t);
    this.stats.draws = 0; this.stats.w = this.w; this.stats.h = this.h;
    this.updateDash(t);
    gl.activeTexture(gl.TEXTURE9); gl.bindTexture(gl.TEXTURE_2D, this.dash?.tex ?? null);
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.masks);

    if (this.shadow) this.drawShadow();

    if (sp.hdr) gl.bindFramebuffer(gl.FRAMEBUFFER, this.ms ? this.ms.fb : this.hdr!.fb);
    else gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.w, this.h);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, this.shadow?.depth ?? null);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    this.drawWorld(t);

    if (!sp.hdr) return;
    const W = this.w, H = this.h;
    if (this.ms) {
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.ms.fb);
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.hdr!.fb);
      gl.blitFramebuffer(0, 0, W, H, 0, 0, W, H, gl.COLOR_BUFFER_BIT, gl.NEAREST);
      gl.blitFramebuffer(0, 0, W, H, 0, 0, W, H, gl.DEPTH_BUFFER_BIT, gl.NEAREST);
    }
    gl.disable(gl.DEPTH_TEST);
    gl.depthMask(false);
    gl.bindVertexArray(this.emptyVao);
    if (this.P.beams && this.beams && !this.stage) this.drawBeams(t);
    if (sp.bloom) this.drawBloom();
    const P = this.P.display!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, W, H);
    gl.useProgram(P.prog);
    gl.activeTexture(gl.TEXTURE4); gl.bindTexture(gl.TEXTURE_2D, this.hdr!.tex);
    gl.activeTexture(gl.TEXTURE5); gl.bindTexture(gl.TEXTURE_2D, this.bloom[0]?.tex ?? null);
    gl.activeTexture(gl.TEXTURE6); gl.bindTexture(gl.TEXTURE_2D, this.beams?.tex ?? null);
    gl.uniform4f(P.u.uD, t % 100, this.exposure, 0.07, sp.grain);
    gl.uniform2f(P.u.uFlags, sp.bloom ? 1 : 0, this.beams && !this.stage ? 1 : 0);
    const ph = this.photo;
    gl.activeTexture(gl.TEXTURE8); gl.bindTexture(gl.TEXTURE_2D, ph && ph.blur > 0 ? this.hdr!.depth : null);
    gl.uniform4f(P.u.uPhoto, ph?.filter ?? 0, Math.max(0.2, ph?.focus ?? 3), ph && this.hdr!.depth ? ph.blur * H : 0, 0);
    gl.uniform2f(P.u.uTexel, 1 / W, 1 / H);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.activeTexture(gl.TEXTURE8); gl.bindTexture(gl.TEXTURE_2D, null); gl.activeTexture(gl.TEXTURE0);
    this.stats.draws++;
    gl.depthMask(true);
  }

  private ground(): Mesh { return this.stage && this.stageMesh ? this.stageMesh : this.porchMesh!; }

  /** the paper's two shapes: flat and square in front of the pumpkin facing the eye, and laid on its skin */
  private paperShapes(s: Slot) {
    const N = PAPER_N, g = s.geo, sq = s.p.shape.squash, n = (N + 1) * (N + 1);
    const flat = new Float32Array(n * 8), skin = new Float32Array(n * 8);
    const toEye = v3.norm(v3.sub(this.eye, s.centre));
    const right = v3.norm(v3.cross([0, 1, 0], toEye)), up = v3.cross(toEye, right);
    const half = g.radius * 1.3, c = v3.add(s.centre, v3.scale(toEye, g.radius * 1.7));
    let last: { p: V3; n: V3 } = { p: [0, 0, g.radius], n: [0, 0, 1] };
    for (let k = 0; k <= N; k++) for (let j = 0; j <= N; j++) {
      const i = k * (N + 1) + j, x = j / N * 2 - 1, y = 1 - k / N * 2;
      const uv = [x * 0.5 + 0.5, 0.5 - y * 0.5];
      flat.set([...v3.add(c, v3.add(v3.scale(right, x * half), v3.scale(up, y * half))), ...toEye, ...uv], i * 8);
      // the inverse of the shader's sheetOf: the direction from the centre this sheet point lies in
      const ph = x * 1.25, th = y, d = v3.norm([Math.sin(ph) * Math.cos(th), Math.sin(th) * sq, Math.cos(ph) * Math.cos(th)]);
      const hit = raycast(g, v3.scale(d, 3), v3.scale(d, -1), true);
      if (hit) last = { p: hit.p, n: hit.n };
      const w = transform(s.model, v3.add(last.p, v3.scale(last.n, 0.004)));
      skin.set([...w, ...rotateDir(s.model, last.n), ...uv], i * 8);
    }
    return { flat, skin };
  }

  private drawPaper(t: number) {
    const gl = this.gl, pa = this.paper!, s = this.slots.find(x => x.id === pa.id);
    if (!s) return;
    let G = this.paperGL;
    if (!G) {
      const N = PAPER_N, idx: number[] = [];
      for (let k = 0; k < N; k++) for (let j = 0; j < N; j++) { const a = k * (N + 1) + j, b = a + N + 1; idx.push(a, b, a + 1, a + 1, b, b + 1); }
      const m = mesh(gl, new Float32Array((N + 1) * (N + 1) * 8), [3, 3, 2], new Uint32Array(idx));
      G = this.paperGL = { mesh: m, tex: gl.createTexture(), key: '', flat: new Float32Array(0), skin: new Float32Array(0), at: new Float32Array((N + 1) * (N + 1) * 8) };
    }
    const key = s.id + ':' + s.keys.shape + ':' + s.p.turn;
    if (G.key !== key) { Object.assign(G, this.paperShapes(s)); G.key = key; }
    // the middle touches first and the edges follow it round
    const N = PAPER_N;
    for (let k = 0; k <= N; k++) for (let j = 0; j <= N; j++) {
      const i = k * (N + 1) + j, x = j / N * 2 - 1, y = 1 - k / N * 2;
      const r = Math.max(Math.abs(x), Math.abs(y) * 0.7);
      let e = clamp(pa.wrap * 1.6 - r * 0.6, 0, 1);
      e = e * e * (3 - 2 * e);
      for (let c = 0; c < 8; c++) G.at[i * 8 + c] = G.flat[i * 8 + c] + (G.skin[i * 8 + c] - G.flat[i * 8 + c]) * e;
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, G.mesh!.buffers[0]);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, G.at);
    gl.activeTexture(gl.TEXTURE7);
    gl.bindTexture(gl.TEXTURE_2D, G.tex);
    if ((pa.canvas as HTMLCanvasElement & { _up?: number })._up !== pa.canvas.width + pa.canvas.height * 1e5 || pa.wrap === 0) {
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, pa.canvas);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      (pa.canvas as HTMLCanvasElement & { _up?: number })._up = pa.canvas.width + pa.canvas.height * 1e5;
    }
    gl.activeTexture(gl.TEXTURE0);
    const P = this.P.paper;
    gl.useProgram(P.prog);
    this.common(P, t);
    gl.uniform1f(P.u.uAlpha, pa.alpha);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.POLYGON_OFFSET_FILL); gl.polygonOffset(-2, -2);
    drawMesh(gl, G.mesh!);
    gl.disable(gl.POLYGON_OFFSET_FILL); gl.disable(gl.BLEND);
    this.stats.draws++;
  }

  /** the stage's spots all aim at the middle of the gourds on show, each frustum just wide enough for them and their shadows */
  private aimSpots() {
    const live = this.slots.filter(s => !this.hidden.has(s.id));
    let c: V3 = [0, 0.3, 0], r = 0.3;
    if (live.length) c = v3.scale(live.reduce((a, s) => v3.add(a, s.centre), [0, 0, 0] as V3), 1 / live.length);
    for (const s of live) r = Math.max(r, Math.hypot(s.centre[0] - c[0], s.centre[2] - c[2]) + s.geo.radius * 1.4);
    c[1] *= 0.6;
    STAGE_SPOTS.forEach((sp, k) => {
      const d = v3.len(v3.sub(sp.at, c));
      mul(perspective(2 * Math.atan(r * 1.3 / d), 1, 0.5, 14), lookAt(sp.at, c), this.spotVP.subarray(k * 16, k * 16 + 16) as M4);
    });
  }

  private drawShadow() {
    const gl = this.gl, sh = this.shadow!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, sh.fb);
    gl.viewport(0, 0, sh.w, sh.h);
    gl.clear(gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.DEPTH_TEST); gl.depthMask(true);
    gl.disable(gl.CULL_FACE);
    // a quarter of the map each: the stage's three spots; on the porch the lantern in the last, the garden spot in the
    // first. On the stage only the gourds cast, the floor just takes their shadows
    const h = sh.w / 2;
    const views: [M4, number, number][] = this.stage
      ? [0, 1, 2].map(k => [this.spotVP.subarray(k * 16, k * 16 + 16), (k & 1) * h, (k >> 1) * h])
      : [[this.lampVP, h, h], [this.spotVP.subarray(0, 16), 0, 0]];
    for (const [vp, x, y] of views) {
      gl.viewport(x, y, h, h);
      this.casters(vp);
    }
  }

  /** the shadow casters seen from one light: stems and the porch as they are, the skins only where they aren't cut */
  private casters(vp: M4) {
    const gl = this.gl, D = this.P.depth, C = this.P.depthCut;
    gl.useProgram(D.prog);
    gl.uniformMatrix4fv(D.u.uVP, false, vp);
    if (!this.stage) { gl.uniformMatrix4fv(D.u.uModel, false, IDENT); drawMesh(gl, this.ground()); }
    for (const s of this.slots) {
      if (this.hidden.has(s.id) || s.stalk) continue;
      gl.uniformMatrix4fv(D.u.uModel, false, s.model);
      drawMesh(gl, s.stem);
      this.stats.draws++;
    }
    gl.useProgram(C.prog);
    gl.uniformMatrix4fv(C.u.uVP, false, vp);
    this.slots.forEach((s, i) => {
      if (this.hidden.has(s.id)) return;
      gl.uniformMatrix4fv(C.u.uModel, false, s.model);
      gl.uniform1i(C.u.uSelf, s.layer);
      gl.uniform1f(C.u.uSquash, s.p.shape.squash);
      drawMesh(gl, s.mesh);
      this.stats.draws++;
    });
  }

  /** each pumpkin's stem and candles, then the inside and the skin; flipped in y for the stage floor's reflection */
  private gourds(t: number, flip: boolean) {
    const gl = this.gl, P = this.P;
    const model = (s: Slot) => flip ? mirrorY(s.model, this.flipM) : s.model;
    this.under = flip;
    gl.useProgram(P.prop.prog);
    this.common(P.prop, t);
    const L = this.lightData;
    this.slots.forEach((s, i) => {
      if (this.hidden.has(s.id)) return;
      gl.uniformMatrix4fv(P.prop.u.uModel, false, model(s));
      gl.uniform1i(P.prop.u.uSelf, i);
      if (!s.stalk) drawMesh(gl, s.stem);
      gl.uniform1i(P.prop.u.uSelf, -1);
      const led = s.p.light.kind === 'led' || s.p.light.kind === 'blink';
      const e = led ? 30 : 0;
      gl.uniform3f(P.prop.u.uEmit, L.pl[i * 3] * e, L.pl[i * 3 + 1] * e, L.pl[i * 3 + 2] * e);
      drawMesh(gl, s.rig);
      this.stats.draws += 2;
    });

    // the pumpkins: the inside first (back faces of the shell pulled in), then the skin with its holes, both sides: its back
    // faces show only past a hole's edge, as the cut face of the flesh
    for (const [prog, inset] of [[P.inside, true], [P.pumpkin, false]] as [Program, boolean][]) {
      gl.useProgram(prog.prog);
      this.common(prog, t);
      if (inset) { gl.enable(gl.CULL_FACE); gl.cullFace(gl.FRONT); } else gl.disable(gl.CULL_FACE);
      // the skin's hole edge as coverage; its alpha is only that, so it isn't written
      const a2c = !inset && !!this.ms;
      if (a2c) { gl.enable(gl.SAMPLE_ALPHA_TO_COVERAGE); gl.colorMask(true, true, true, false); }
      this.slots.forEach((s, i) => {
        if (this.hidden.has(s.id)) return;
        gl.uniformMatrix4fv(prog.u.uModel, false, model(s));
        gl.uniform1i(prog.u.uSelf, i);
        gl.uniform1f(prog.u.uInset, inset ? s.p.wall : 0);
        if (!inset) {
          const sh = s.p.shape;
          gl.uniform3fv(prog.u.uSkin, sh.skin);
          gl.uniform4f(prog.u.uLook, sh.ribTint, s.p.wall, KIND_ID[sh.kind] ?? 0, sh.seed % 1000);
          const pt = sh.pattern;
          gl.uniform3fv(prog.u.uBase2, pt?.base2 ?? sh.skin);
          gl.uniform4f(prog.u.uGrooveCol, pt?.groove?.[0] ?? 0, pt?.groove?.[1] ?? 0, pt?.groove?.[2] ?? 0, pt?.groove ? 1 : 0);
          gl.uniform4f(prog.u.uPat, pt?.speckle ?? 0, pt?.stripes ?? 0, pt?.bicolor ?? 0, pt?.mottle ?? 0);
          gl.uniform4f(prog.u.uPat2, pt?.stripeN ?? 16, pt?.marble ?? 0, this.trace ? 1 : 0, this.dots === s.id && this.dash?.key ? 1 : 0);
        }
        drawMesh(gl, s.mesh);
        this.stats.draws++;
      });
      if (a2c) { gl.disable(gl.SAMPLE_ALPHA_TO_COVERAGE); gl.colorMask(true, true, true, true); }
    }
    gl.cullFace(gl.BACK);
    gl.disable(gl.CULL_FACE);
    // the cut faces
    const C = P.cut;
    gl.useProgram(C.prog);
    this.common(C, t);
    this.slots.forEach((s, i) => {
      if (this.hidden.has(s.id)) return;
      const m = this.cutWall(s, s.layer);
      if (!m) return;
      gl.uniformMatrix4fv(C.u.uModel, false, model(s));
      gl.uniform1i(C.u.uSelf, i);
      gl.uniform3fv(C.u.uSkin, s.p.shape.skin);
      drawMesh(gl, m);
      this.stats.draws++;
    });
    this.under = false;
  }

  private drawWorld(t: number) {
    const gl = this.gl, P = this.P;
    // the sky behind everything
    gl.disable(gl.DEPTH_TEST); gl.depthMask(false); gl.disable(gl.CULL_FACE); gl.disable(gl.BLEND);
    if (!this.stage) {
      gl.useProgram(P.sky.prog);
      this.common(P.sky, t);
      gl.uniformMatrix4fv(P.sky.u.uInvVP, false, this.invVP);
      gl.bindVertexArray(this.emptyVao);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    gl.enable(gl.DEPTH_TEST); gl.depthMask(true); gl.depthFunc(gl.LESS);

    // props: the porch, then each pumpkin's stem and candles
    gl.useProgram(P.prop.prog);
    this.common(P.prop, t);
    const po = this.porch!, lk = po.lamp.on ? po.lamp.brightness * 3.8 : 0;
    gl.uniform3f(P.prop.u.uEmit, lk, lk, lk);
    gl.uniform1i(P.prop.u.uSelf, -1);
    if (this.stage) gl.uniform3f(P.prop.u.uEmit, 0, 0, 0);
    // a polished floor on the stage: the gourds drawn upside down under it first, then the floor added over them,
    // so a faint reflection shows through. Not on the plain tier, and not from underneath
    const below = this.stage && this.eye[1] < 0.02, mirror = !!this.stage && !below && this.spec.hdr && this.spec.mirror;
    if (mirror) {
      gl.frontFace(gl.CW);
      this.gourds(t, true);
      gl.frontFace(gl.CCW);
      gl.clear(gl.DEPTH_BUFFER_BIT);
      gl.useProgram(P.prop.prog);
      this.common(P.prop, t);
      gl.uniform3f(P.prop.u.uEmit, 0, 0, 0);
      gl.uniform1i(P.prop.u.uSelf, -1);
      gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.CONSTANT_COLOR); gl.blendColor(REFLECT, REFLECT, REFLECT, 1);
    }
    gl.uniformMatrix4fv(P.prop.u.uModel, false, IDENT);
    // seen from underneath (the carve step's spin), the stage floor would hide the gourd
    if (!below) drawMesh(gl, this.ground());
    gl.disable(gl.BLEND);
    this.stats.draws++;
    this.gourds(t, false);
    gl.cullFace(gl.BACK);
    gl.disable(gl.CULL_FACE);

    if (po.grass && this.spec.grass > 0 && !this.stage) this.drawGrass(t);
    if (this.paper) this.drawPaper(t);

    // the flames, added over everything solid
    const fl: number[] = [];
    this.slots.forEach((s, i) => {
      const l = s.p.light, led = l.kind === 'led' || l.kind === 'blink';
      if (!this.lit || this.hidden.has(s.id)) return;
      for (const f of s.flames) {
        const w = transform(s.model, f);
        const k = led ? s.flick : s.flick * s.flick;
        fl.push(w[0], w[1], w[2], led ? 0.022 : 0.011 * (0.85 + 0.15 * s.flick),
          l.color[0] * l.brightness * 40 * k, l.color[1] * l.brightness * 40 * k, l.color[2] * l.brightness * 40 * k, led ? 1 : 0);
      }
      void i;
    });
    if (fl.length) {
      gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE); gl.depthMask(false);
      gl.useProgram(P.flame.prog);
      const vi = invert(lookAt(this.eye, this.camera.target));
      gl.uniformMatrix4fv(P.flame.u.uVP, false, this.vp);
      gl.uniform3f(P.flame.u.uRight, vi[0], vi[1], vi[2]);
      gl.uniform3f(P.flame.u.uUp, vi[4], vi[5], vi[6]);
      gl.uniform1f(P.flame.u.uTime, t);
      gl.uniform1f(P.flame.u.uExposure, this.exposure);
      gl.bindVertexArray(this.flameVao);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.flameBuf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(fl), gl.DYNAMIC_DRAW);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, fl.length / 8);
      gl.disable(gl.BLEND); gl.depthMask(true);
      this.stats.draws++;
    }
    gl.bindVertexArray(null);
  }

  // ---- grass: liminality's bands, made from the cells in view each frame

  private cellData = new Int32Array(512 * 2);

  private drawGrass(t: number) {
    const gl = this.gl, P = this.P.grass, sp = this.spec;
    const CELL = 2, density = 380 * sp.grass, width = 0.007 / Math.sqrt(sp.grass), height = 0.2;
    const pixel = 2 * Math.tan(this.fovy / 2) / this.h, pixels = 1.5;
    const full = width / (pixels * pixel);
    const fwd = v3.norm(v3.sub(this.camera.target, this.eye));
    const bandEdge = [0, 5, 9, 16];
    const segs = sp.grass >= 0.5 ? [5, 4, 3, 2] : [4, 3, 2, 1];
    const bands: [number, number][][] = [[], [], [], []];
    const aspect = this.w / Math.max(1, this.h), cone = Math.atan(Math.tan(this.fovy / 2) * Math.hypot(1, aspect));
    const eye = this.eye, box = { x: HOUSE_X, z0: WALL - HOUSE_D, z1: WALL, y: 3.4 };
    /** the line from the eye to (x, 0.35, z) passes through the house's walls */
    const through = (x: number, z: number) => {
      let t0 = 0, t1 = 1;
      for (const [o, e, lo, hi] of [[eye[0], x, -box.x, box.x], [eye[1], 0.35, 0, box.y], [eye[2], z, box.z0, box.z1]]) {
        const dd = e - o;
        if (Math.abs(dd) < 1e-6) { if (o < lo || o > hi) return false; continue; }
        let a = (lo - o) / dd, b = (hi - o) / dd;
        if (a > b) [a, b] = [b, a];
        t0 = Math.max(t0, a); t1 = Math.min(t1, b);
        if (t0 > t1) return false;
      }
      return true;
    };
    const hidden = (x0: number, z0: number) => through(x0, z0) && through(x0 + CELL, z0) && through(x0, z0 + CELL) && through(x0 + CELL, z0 + CELL);
    for (let cz = -12; cz < 8; cz++) for (let cx = -12; cx < 12; cx++) {
      const x0 = cx * CELL, z0 = cz * CELL;
      if (z0 + CELL <= 0.05 && z0 >= WALL - 2 && x0 >= -DECK_X - 0.2 && x0 + CELL <= DECK_X + 0.2) continue;   // all under the deck
      if (z0 >= WALL - HOUSE_D && z0 + CELL <= WALL && x0 >= -HOUSE_X && x0 + CELL <= HOUSE_X) continue;     // all under the house
      const c: V3 = [x0 + 1, 0, z0 + 1];
      const to = v3.sub(c, this.eye), d = v3.len(to);
      if (d > 22) continue;
      if (v3.dot(to, fwd) < -1.6) continue;
      // within the view's cone, loosely: a cell is about 1.5m from its middle to a corner
      if (d > 1.6 && v3.dot(to, fwd) / d < Math.cos(Math.min(Math.PI, cone + Math.asin(Math.min(1, 1.6 / d))))) continue;
      // and not wholly behind the house
      if (hidden(x0, z0)) continue;
      const b = d < bandEdge[1] ? 0 : d < bandEdge[2] ? 1 : d < bandEdge[3] ? 2 : 3;
      bands[b].push([cx, cz]);
    }
    let n = 0;
    const offs: number[] = [];
    for (const b of bands) { offs.push(n); for (const c of b) { this.cellData[n * 2] = c[0]; this.cellData[n * 2 + 1] = c[1]; n++; } }
    if (!n) return;
    gl.activeTexture(gl.TEXTURE2);
    if (!this.cells) {
      this.cells = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, this.cells);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RG32I, 512, 1);
    }
    gl.bindTexture(gl.TEXTURE_2D, this.cells);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 512, 1, gl.RG_INTEGER, gl.INT, this.cellData);
    gl.useProgram(P.prog);
    this.common(P, t);
    gl.uniform4f(P.u.uLook, density, height, width, 0.45);
    gl.uniform4f(P.u.uMore, 22, pixels, 2.4, pixel);
    gl.uniform4f(P.u.uVary, 7, 0.8, 0.5, 1);
    const bare = new Float32Array(16).fill(-999);
    BARE.forEach((b, i) => bare.set([b[0], b[1], b[2], b[3]], i * 4));
    gl.uniform4fv(P.u.uBare, bare);
    gl.uniform2f(P.u.uDrift, 2.2, 0.8);
    gl.bindVertexArray(this.emptyVao);
    this.stats.blades = 0;
    bands.forEach((cells, b) => {
      if (!cells.length) return;
      const keep = Math.min(1, full / Math.max(bandEdge[b], 0.3));
      const perCell = Math.ceil(density * keep * CELL * CELL);
      gl.uniform4i(P.u.uBand, offs[b], perCell, segs[b], 7);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, (segs[b] + 1) * 2, cells.length * perCell);
      this.stats.blades += cells.length * perCell;
      this.stats.draws++;
    });
  }

  // ---- post

  private drawBeams(t: number) {
    const gl = this.gl, P = this.P.beams!, b = this.beams!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, b.fb);
    gl.viewport(0, 0, b.w, b.h);
    gl.useProgram(P.prog);
    this.common(P, t);
    gl.uniformMatrix4fv(P.u.uInvVP, false, this.invVP);
    gl.activeTexture(gl.TEXTURE3); gl.bindTexture(gl.TEXTURE_2D, this.hdr!.depth);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    this.stats.draws++;
  }

  private drawBloom() {
    const gl = this.gl, D = this.P.down!, U = this.P.up!;
    gl.useProgram(D.prog);
    let src = this.hdr!.tex, sw = this.w, sh = this.h;
    gl.activeTexture(gl.TEXTURE3);
    for (const t of this.bloom) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, t.fb);
      gl.viewport(0, 0, t.w, t.h);
      gl.bindTexture(gl.TEXTURE_2D, src);
      gl.uniform2f(D.u.uTexel, 1 / sw, 1 / sh);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      src = t.tex; sw = t.w; sh = t.h;
    }
    gl.useProgram(U.prog);
    gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE);
    for (let k = this.bloom.length - 1; k > 0; k--) {
      const lo = this.bloom[k], hi = this.bloom[k - 1];
      gl.bindFramebuffer(gl.FRAMEBUFFER, hi.fb);
      gl.viewport(0, 0, hi.w, hi.h);
      gl.bindTexture(gl.TEXTURE_2D, lo.tex);
      gl.uniform2f(U.u.uTexel, 1 / lo.w, 1 / lo.h);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    gl.disable(gl.BLEND);
    this.stats.draws += this.bloom.length * 2;
  }

  /**
   * The scene as it is now, at its own size (default 1200 x 630, the social card), with the current camera, as a JPEG
   * data URL. Drawn and read in the same task, so the unpreserved drawing buffer is still full; the canvas goes back to
   * its size on screen and is redrawn afterwards.
   */
  snapshot(w = 1200, h = 630, t = this.lastT, quality = 0.85): string {
    if (!this.porch) return '';
    this.forced = [Math.round(w), Math.round(h)];
    try {
      this.frame(t);
      return this.canvas.toDataURL('image/jpeg', quality);
    } finally {
      this.forced = null;
      this.frame(t);
    }
  }

  /** read one pixel of what's on screen after the next frame, for tests: averages a small square */
  probe(x: number, y: number, t: number, r = 3): [number, number, number] {
    const gl = this.gl;
    this.frame(t);
    const px = Math.round(x * this.w), py = Math.round((1 - y) * this.h);
    const out = new Uint8Array((r * 2 + 1) * (r * 2 + 1) * 4);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.readPixels(px - r, py - r, r * 2 + 1, r * 2 + 1, gl.RGBA, gl.UNSIGNED_BYTE, out);
    const s = [0, 0, 0];
    for (let i = 0; i < out.length; i += 4) { s[0] += out[i]; s[1] += out[i + 1]; s[2] += out[i + 2]; }
    const k = out.length / 4;
    return [s[0] / k, s[1] / k, s[2] / k];
  }

  /** after a lost context comes back: everything again from the scene */
  restore() {
    this.P = undefined as unknown as Programs;
    this.masks = null; this.cells = null; this.flameBuf = null; this.flameVao = null; this.emptyVao = null;
    this.porchMesh = null; this.shadow = null; this.ms = null; this.hdr = null; this.beams = null; this.bloom = [];
    this.slots = [];
    this.parked.clear();
    this.w = 0;
    this.setTier(this.tier);
  }
}

const PAPER_N = 24;
/** the shape at its size on the porch */
const sized = (p: PlacedPumpkin) => (p.scale && p.scale !== 1 ? { ...p.shape, size: p.shape.size * p.scale } : p.shape);
/** how much of the gourds the stage floor reflects */
const REFLECT = 0.24;
/** m with world y flipped (the floor is y = 0 on the stage), into out */
function mirrorY(m: M4, out: M4): M4 { out.set(m); out[1] = -m[1]; out[5] = -m[5]; out[9] = -m[9]; out[13] = -m[13]; return out; }
const IDENT = (() => { const m = new Float32Array(16); m[0] = m[5] = m[10] = m[15] = 1; return m; })();
const KIND_ID: Record<string, number> = { field: 0, tall: 1, cinderella: 2, ghost: 3, jarrahdale: 4, warty: 5, mini: 6, bottle: 7, crookneck: 8, turban: 9, musquee: 10, shishigatani: 11, pear: 12, thorns: 13, swan: 14, bicolor: 15, hound: 16 };
